import { useState } from 'react';
import { createPortal } from 'react-dom';

import styled from 'styled-components';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import { ReactComponent as CrownSvg } from '../../../image/StatisticsCrown.svg';
import { ReactComponent as AddCardSvg } from '../../../image/StatisticsAddCard.svg';
import { ReactComponent as CurrencyExchangeSvg } from '../../../image/StatisticsCurrencyExchange.svg';

// 연령대 index(0=10대 이하 ~ 9=90대) → 번역 키 //
const AGE_LABEL_KEYS = ['ageUnder10', 'age10s', 'age20s', 'age30s', 'age40s', 'age50s', 'age60s', 'age70s', 'age80s', 'age90s'];
// 연령대 index → 스택 바 세그먼트 색상 키 (라이트: 연령 높을수록 연한 그린 / 다크: 연령 높을수록 진한 그린) //
const AGE_SEGMENT_COLOR_KEYS_LIGHT = ['brand500', 'brand400', 'brand350', 'brand300', 'brand250', 'brand200', 'brand150', 'brand100', 'brand100', 'brand100'];
const AGE_SEGMENT_COLOR_KEYS_DARK = ['brand500', 'brand550', 'brand600', 'brand650', 'brand700', 'brand750', 'brand800', 'brand850', 'brand900', 'brand900'];
// 순위(rank) 0~2 뱃지 색상 키 (라이트: 밝은 그린 / 다크: 진한 그린) //
const RANK_BADGE_COLOR_KEYS_LIGHT = ['brand500', 'brand400', 'brand300'];
const RANK_BADGE_COLOR_KEYS_DARK = ['brand500', 'brand600', 'brand700'];

// 회원권 선호도 표시 전용 뷰 (집계 완료된 값만 받아 렌더) //
// - 통계: MembershipPreferenceBox (원본 → 집계) 가 사용 //
// - AI 채팅: MembershipPreferenceRenderer (백엔드 집계 결과) 가 사용 //
export default function MembershipPreferenceView(props) {
  const {
    rankings,          // [{ membershipId, membershipName, count, segments: [{ ageIndex, count, percent }] }]
    totalCount,        // 가입 회원 총 카운트 (회원권별 인원 합) //
    totalRevenue,      // 회원권 매출 총합 //
    topCards,          // { mostSelected:{name,count}, topRevenue:{name,revenue}, topReReg:{name,rate} } //
    avgReRegRate,      // 평균 재등록률 (0~1) //
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  // 라이트/다크 모드별 그린 팔레트 선택 (라이트는 피그마 디자인 기준 밝은 그린) //
  const AGE_SEGMENT_COLOR_KEYS = isDark ? AGE_SEGMENT_COLOR_KEYS_DARK : AGE_SEGMENT_COLOR_KEYS_LIGHT;
  const RANK_BADGE_COLOR_KEYS = isDark ? RANK_BADGE_COLOR_KEYS_DARK : RANK_BADGE_COLOR_KEYS_LIGHT;

  const [tooltipRow, setTooltipRow] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // 숫자 콤마 포맷팅 //
  function formatNumber(num) {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // 전체 대비 비율 계산 //
  function calcShare(value, total) {
    if (total === 0) return 0;

    return value / total * 100;
  }

  // 스택 바 호버 시 툴팁 표시 + 마우스 위치 추적 //
  function onMouseMoveBar(e, row) {
    setTooltipRow(row);
    setTooltipPosition({ x: e.clientX, y: e.clientY });
  }

  // 스택 바에서 벗어나면 툴팁 숨김 //
  function onMouseLeaveBar() {
    setTooltipRow(null);
  }

  // 툴팁 세그먼트를 3개씩 열로 분할 (3줄 초과 시 오른쪽으로 열 추가) //
  function chunkSegmentsToColumns(segments) {
    const columns = [];

    for (let i = 0; i < segments.length; i += 3) {
      columns.push(segments.slice(i, i + 3));
    }

    return columns;
  }

  // 화면 가장자리에서 툴팁이 잘리지 않도록 위치 보정 //
  function getTooltipStyle() {
    const isNearRight = tooltipPosition.x > window.innerWidth - 340;
    const isNearBottom = tooltipPosition.y > window.innerHeight - 200;

    return {
      left: tooltipPosition.x + (isNearRight ? -16 : 16),
      top: tooltipPosition.y + (isNearBottom ? -16 : 16),
      transform: 'translate(' + (isNearRight ? '-100%' : '0') + ', ' + (isNearBottom ? '-100%' : '0') + ')',
    };
  }

  // 상단 카드 렌더링 //
  function renderTopCard(icon, label, name, mainText, subText) {
    return (
      <TopCard>
        <TopCardLabelRow>
          {icon}
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{label}</Text>
        </TopCardLabelRow>
        <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text}>{name}</Text>
        <TopCardValueRow>
          <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.subText}>{mainText}</Text>
          <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText}>{subText}</Text>
        </TopCardValueRow>
      </TopCard>
    );
  }

  // 스택 바 세그먼트 렌더링 (10% 미만은 정보 생략하고 ...만 표시) //
  function renderSegment(segment, segmentIndex) {
    const segmentColor = theme[AGE_SEGMENT_COLOR_KEYS[segment.ageIndex]] || theme.brand700;
    const isTooNarrow = segment.percent < 10;

    return (
      <BarSegment key={'Segment-' + segmentIndex} $flex={segment.count} $color={segmentColor}>
        {
          isTooNarrow &&
          <SegmentLabel fontFamily='NotoSansKR-Bold' fontSize={9} color={theme.white}>...</SegmentLabel>
        }
        {
          !isTooNarrow &&
          <SegmentAgeLabel fontFamily='NotoSansKR-Bold' fontSize={9} color={theme.white}>
            {t('statistics.engagement.' + AGE_LABEL_KEYS[segment.ageIndex])}
          </SegmentAgeLabel>
        }
        {
          !isTooNarrow &&
          <SegmentValueWrapper>
            <SegmentLabel fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.white}>{segment.percent}</SegmentLabel>
            <SegmentLabel fontFamily='NotoSansKR-Medium' fontSize={9} color={theme.white}>%</SegmentLabel>
          </SegmentValueWrapper>
        }
      </BarSegment>
    );
  }

  // 회원권 랭킹 행 렌더링 //
  function renderRankingRow(row, index) {
    const badgeColor = index < 3 ? theme[RANK_BADGE_COLOR_KEYS[index]] : theme.thirdBackground;
    const badgeTextColor = index < 3 ? theme.subBackground : theme.text;

    return (
      <RankingRow key={'MembershipRanking-' + index}>
        <RankBadge $bgColor={badgeColor}>
          <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={badgeTextColor}>{index + 1}</Text>
        </RankBadge>
        <RankNameWrapper>
          <RankNameText fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.text}>{row.membershipName}</RankNameText>
        </RankNameWrapper>
        <StackedBarTrack
          onMouseMove={(e) => onMouseMoveBar(e, row)}
          onMouseLeave={onMouseLeaveBar}>
          {row.segments.map((segment, segmentIndex) => renderSegment(segment, segmentIndex))}
        </StackedBarTrack>
        <RankCountWrapper>
          <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text}>{formatNumber(row.count)}</Text>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('statistics.membershipPreference.memberUnit')}</Text>
        </RankCountWrapper>
      </RankingRow>
    );
  }

  // 상단 카드 데이터 텍스트 정리 //
  function getCardTexts() {
    const mostSelected = topCards?.mostSelected;
    const topRevenue = topCards?.topRevenue;
    const topReReg = topCards?.topReReg;

    return {
      mostSelected: {
        name: mostSelected ? mostSelected.name : '-',
        mainText: mostSelected ? formatNumber(mostSelected.count) + t('statistics.membershipPreference.memberUnit') : '-',
        subText: mostSelected ? t('statistics.membershipPreference.ofTotal', { percent: calcShare(mostSelected.count, totalCount).toFixed(1) }) : '',
      },
      topRevenue: {
        name: topRevenue ? topRevenue.name : '-',
        mainText: topRevenue ? Math.round(calcShare(topRevenue.revenue, totalRevenue)) + '%' : '-',
        subText: topRevenue ? t('statistics.membershipPreference.totalRevenue', { amount: formatNumber(topRevenue.revenue) }) : '',
      },
      topReReg: {
        name: topReReg ? topReReg.name : '-',
        mainText: topReReg ? Math.round(topReReg.rate * 100) + '%' : '-',
        subText: topReReg ? t('statistics.membershipPreference.avgReRegistration', { percent: Math.round(avgReRegRate * 100) }) : '',
      },
    };
  }

  const cardTexts = getCardTexts();

  return (
    <>
      <TopCardRow>
        {renderTopCard(<StyledCrownIcon />, t('statistics.membershipPreference.mostSelected'), cardTexts.mostSelected.name, cardTexts.mostSelected.mainText, cardTexts.mostSelected.subText)}
        {renderTopCard(<StyledAddCardIcon />, t('statistics.membershipPreference.topRevenue'), cardTexts.topRevenue.name, cardTexts.topRevenue.mainText, cardTexts.topRevenue.subText)}
        {renderTopCard(<StyledCurrencyExchangeIcon />, t('statistics.membershipPreference.topReRegistration'), cardTexts.topReReg.name, cardTexts.topReReg.mainText, cardTexts.topReReg.subText)}
      </TopCardRow>
      <SubtitleRow>
        <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text}>
          {t('statistics.membershipPreference.selectedAmongJoined')}
        </Text>
        <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text}>
          {t('statistics.membershipPreference.totalMembers', { count: formatNumber(totalCount) })}
        </Text>
      </SubtitleRow>
      <RankingListWrapper>
        {rankings.map((row, index) => renderRankingRow(row, index))}
      </RankingListWrapper>
      {
        tooltipRow &&
        createPortal(
          // transform 조상(예: AI 채팅 AiMessageAppear의 animation forwards) 아래에서 position:fixed가
          // 밀리는 문제 방지 — document.body로 portal해 뷰포트(마우스) 기준 정렬 유지 //
          <TooltipWrapper style={getTooltipStyle()}>
            <TooltipTitleBox>
              <TooltipTitleText fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                {tooltipRow.membershipName}
              </TooltipTitleText>
            </TooltipTitleBox>
            <TooltipColumns>
              {
                chunkSegmentsToColumns(tooltipRow.segments).map((column, columnIndex) => (
                  <TooltipColumn key={'TooltipColumn-' + columnIndex}>
                    {
                      column.map((segment) => (
                        <TooltipEntry key={'TooltipEntry-' + segment.ageIndex}>
                          <TooltipColorBox $color={theme[AGE_SEGMENT_COLOR_KEYS[segment.ageIndex]] || theme.brand700} />
                          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText}>
                            {t('statistics.engagement.' + AGE_LABEL_KEYS[segment.ageIndex])}
                          </Text>
                          <TooltipPercentWrapper>
                            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText}>{segment.percent}</Text>
                            <Text fontFamily='NotoSansKR-Medium' fontSize={10} color={theme.subText}>%</Text>
                          </TooltipPercentWrapper>
                        </TooltipEntry>
                      ))
                    }
                  </TooltipColumn>
                ))
              }
            </TooltipColumns>
          </TooltipWrapper>,
          document.body
        )
      }
    </>
  );
}

const TopCardRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  width: 100%;

  gap: 20px;

  opacity: 0.8;
`;

const TopCard = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  overflow: hidden;

  flex: 1;
  min-width: 0;

  padding: 15px;

  gap: 10px;

  border-radius: 10px;
  background-color: ${props => props.theme.thirdBackground};
`;

const TopCardLabelRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 10px;
`;

const TopCardValueRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  overflow: hidden;

  gap: 10px;
`;

const SubtitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;

  width: 100%;
`;

const RankingListWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;

  width: 100%;

  gap: 10px;
`;

const RankingRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  width: 100%;

  gap: 10px;
`;

const RankBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 25px;
  height: 25px;

  flex-shrink: 0;

  border-radius: 4px;
  background-color: ${props => props.$bgColor};
`;

const RankNameWrapper = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;

  width: 160px;

  flex-shrink: 0;
`;

const RankNameText = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: default;
`;

const StackedBarTrack = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  overflow: hidden;

  flex: 1;
  min-width: 0;

  height: 25px;

  border-radius: 5px;
  background-color: ${props => props.theme.buttonFill3};

  cursor: pointer;
`;

const BarSegment = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  justify-content: center;
  overflow: hidden;

  flex: ${props => props.$flex} 0 0;
  min-width: 40px;

  height: 100%;

  padding: 0 2px;

  gap: 5px;

  background-color: ${props => props.$color};
`;

const SegmentValueWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  gap: 2px;
`;

const SegmentLabel = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;
  white-space: nowrap;
  cursor: pointer;
`;

const SegmentAgeLabel = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: pointer;
`;

const RankCountWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  justify-content: flex-end;
  overflow: hidden;

  width: 60px;

  flex-shrink: 0;

  gap: 2px;
`;

const StyledCrownIcon = styled(CrownSvg)`
  width: 24px;
  height: 24px;

  flex-shrink: 0;

  & path {
    fill: ${props => props.theme.brand500};
  }
`;

const StyledAddCardIcon = styled(AddCardSvg)`
  width: 24px;
  height: 24px;

  flex-shrink: 0;

  & path {
    fill: ${props => props.theme.brand500};
  }
`;

const StyledCurrencyExchangeIcon = styled(CurrencyExchangeSvg)`
  width: 24px;
  height: 24px;

  flex-shrink: 0;

  & path {
    fill: ${props => props.theme.brand500};
  }
`;

const TooltipWrapper = styled.div`
  position: fixed;
  z-index: 1000;

  display: flex;
  flex-direction: column;

  align-items: flex-start;

  padding: 10px;

  gap: 8px;

  border-radius: 5px;
  background-color: ${props => props.theme.background};
  box-shadow: 0px 4px 50px 0px rgba(0, 0, 0, 0.05);

  pointer-events: none;
  white-space: nowrap;
`;

const TooltipTitleBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  box-sizing: border-box;
  width: 100%;

  padding: 4px 8px;

  border-radius: 2px;
  background-color: ${props => props.theme.buttonFill};
`;

const TooltipTitleText = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;
  text-align: center;

  word-break: break-word;
  cursor: default;
`;

const TooltipColumns = styled.div`
  display: flex;
  flex-direction: row;

  align-items: flex-start;

  gap: 12px;
`;

const TooltipColumn = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;

  gap: 6px;
`;

const TooltipEntry = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 6px;
`;

const TooltipPercentWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: baseline;

  gap: 1px;
`;

const TooltipColorBox = styled.div`
  width: 12px;
  height: 12px;

  flex-shrink: 0;

  border-radius: 2px;
  background-color: ${props => props.$color};
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;
  cursor: default;
`;
